import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, ChevronRight, CalendarCheck } from "lucide-react";
import { format } from "date-fns";
import { Link, useNavigate } from "react-router-dom";

const typeLabels = {
  personal_training: "Personal",
  group_class: "Group",
  open_gym: "Open Gym",
  assessment: "Assessment",
};

const typeColors = {
  personal_training: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  group_class: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  open_gym: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  assessment: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

export default function UpcomingSessions({ sessions = [], trainers = [] }) {
  const navigate = useNavigate();
  const trainerName = (id) => trainers.find((t) => t.id === id)?.name || id;

  const upcoming = sessions
    .filter((s) => s.status === "upcoming")
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 4);

  return (
    <div
      data-testid="upcoming-sessions-section"
      className="rounded-3xl p-5 sm:p-6 text-white space-y-4 shadow-xl border border-white/10"
      style={{
        background: "radial-gradient(ellipse at top, #0E1B2F 0%, #070D18 100%)",
      }}
    >
      {/* SECTION HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-sky-400">
            SCHEDULE & COACHING
          </p>
          <h3 className="text-xl sm:text-2xl font-display font-black text-white tracking-tight">
            Upcoming Sessions
          </h3>
        </div>

        <Link
          to="/sessions"
          data-testid="view-all-sessions-btn"
          className="px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-bold text-sky-300 flex items-center gap-1.5 transition active:scale-95"
        >
          <span>View All</span>
          <ChevronRight className="w-3.5 h-3.5 opacity-70" />
        </Link>
      </div>

      {/* COMPACT APPOINTMENT ROWS */}
      <div className="space-y-2.5">
        {upcoming.length === 0 ? (
          <div className="text-center py-7 px-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <CalendarCheck className="w-9 h-9 mx-auto text-slate-500" />
            <div>
              <p className="text-sm font-bold text-slate-200">
                No upcoming sessions
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Book a 1-on-1 coaching session, group class, or assessment.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/sessions")}
              className="px-4 py-2 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 border border-sky-400/40 text-xs font-bold text-sky-300 transition active:scale-95 inline-flex items-center gap-1.5"
            >
              <Calendar className="w-3.5 h-3.5" /> Book Session
            </button>
          </div>
        ) : (
          upcoming.map((session) => {
            const sessionDate = new Date(session.date);
            const isInvalidDate = isNaN(sessionDate.getTime());

            return (
              <div
                key={session.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate("/sessions")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate("/sessions");
                  }
                }}
                className="flex items-center gap-3.5 p-3 sm:p-3.5 rounded-2xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 transition-all cursor-pointer group select-none"
              >
                {/* Compact Date Box */}
                <div className="w-12 h-12 rounded-xl bg-sky-500/10 border border-sky-400/20 flex flex-col items-center justify-center shrink-0 group-hover:border-sky-400/50 transition-colors">
                  <span className="text-[9px] font-black uppercase text-slate-400 leading-tight">
                    {isInvalidDate ? "DAY" : format(sessionDate, "MMM")}
                  </span>
                  <span className="text-base font-black font-display text-sky-400 leading-tight">
                    {isInvalidDate ? "--" : format(sessionDate, "d")}
                  </span>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm sm:text-base text-white truncate">
                    {session.title || "Training Session"}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                    <span className="flex items-center gap-1 shrink-0">
                      <Clock className="w-3 h-3 text-slate-500" />
                      {isInvalidDate ? "Scheduled" : format(sessionDate, "h:mm a")}
                    </span>
                    {session.trainer && (
                      <>
                        <span className="text-slate-600">·</span>
                        <span className="flex items-center gap-1 truncate text-slate-300">
                          <User className="w-3 h-3 text-slate-500" />
                          {trainerName(session.trainer)}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                {session.session_type && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] uppercase font-bold shrink-0 ${
                      typeColors[session.session_type] ||
                      "bg-white/10 text-slate-300 border-white/20"
                    }`}
                  >
                    {typeLabels[session.session_type] || session.session_type}
                  </Badge>
                )}

                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-sky-300 shrink-0 transition-colors" />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}